import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useAtom } from 'jotai';
import { omit } from 'lodash';
import { useEffect } from 'react';

import { PERMISSIONS } from '@/constants/auth.constants';
import useHandleError from '@/hooks/useHandleError';
import type {
  User,
  UserDTO,
  UserEditDialogFormFields
} from '@/pages/Administration/Users';
import {
  mapUserDTO,
  roleTypesAtom
} from '@/pages/Administration/Users/Users.store';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import { axiosClient } from '@/services/BaseApi.service';
import { checkPermissions } from '@/services/permissions';
import { userAtom } from '@/store/user.store';

export const useUserEditDialog = (userData: User) => {
  const [roleTypes] = useAtom(roleTypesAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [currentUser] = useAtom(userAtom);
  const handleError = useHandleError();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (userInfo: UserEditDialogFormFields) => {
      const CURRENT_USER_CAN_UPDATE_NOTIFICATIONS = checkPermissions(
        [PERMISSIONS.USERS_NOTIFICATIONS],
        currentUser,
        selectedOrganization
      );

      const userReqPayload = {
        ...(CURRENT_USER_CAN_UPDATE_NOTIFICATIONS
          ? {
              // users who can update notification settings
              ...userInfo
            }
          : {
              // users who cannot update notification settings
              ...omit(userInfo, [
                'notification_announcement',
                'notification_ticket_service',
                'notification_ticket_threat',
                'notification_threat_priority'
              ])
            }),
        ...omit(userInfo, ['sms_consent', 'tos_consent']),
        name: userInfo.name?.trim(),
        email: userInfo.email?.trim(),
        organization_id: selectedOrganization.id
      };

      if (userReqPayload.id === -1) {
        return axiosClient.post(`/users/`, userReqPayload);
      }
      return axiosClient.put(`/users/${userReqPayload.id}`, userReqPayload);
    },
    onSuccess: () => {
      // refetch data
      queryClient.invalidateQueries({
        queryKey: [`organizations/${selectedOrganization.label}/watchers`]
      });
      queryClient.invalidateQueries({
        queryKey: [
          `organizations/${selectedOrganization?.label}/users?exclude_service_accounts=true`
        ]
      });
      queryClient.invalidateQueries({
        queryKey: [
          `organizations/${selectedOrganization.label}/check-notifications`
        ]
      });
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to update user.');
    }
  });

  const { data, error, isError, refetch } = useQuery<UserDTO, AxiosError, User>(
    {
      queryKey: [`users/${userData.id}`],
      enabled: false,
      select: (data) => mapUserDTO(data, selectedOrganization.label)
    }
  );

  useEffect(() => {
    if (isError) {
      handleError(error, 'Failure to fetch user data.');
    }
  }, [error, isError]);

  return {
    mutation,
    refetch,
    roleTypes,
    user: data ?? userData
  };
};

export default useUserEditDialog;
